"use client";

import { medimoQC } from "@medimo/core-api/internal/next";
import { useGlobalTranscriptionStatus } from "@medimo/global-state";
import {
  StyledEditor,
  extensionsWithLink,
  useEditor,
} from "@medimo/shared-editor";
import {
  Button,
  Dialog,
  DialogCloseButton,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@medimo/ui";
import { usePathname } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import { usePopupNotifications } from "../hooks/use-notifications";
import { useInvalidateUnreadStatus } from "../hooks/use-unread-status";

// お知らせダイアログ（通常種別のみ。リリース通知は release-dialog が担当）
// ログインフラグの読み取り・削除は NotificationCoordinator に集約されており、
// このコンポーネントは isJustLoggedIn を受け取るだけで表示挙動は従来どおり
export const NotificationDialog = ({
  isJustLoggedIn,
}: {
  isJustLoggedIn: boolean;
}) => {
  const transcribingStatus = useGlobalTranscriptionStatus(
    (state) => state.status,
  );
  const pathname = usePathname();

  const [open, setOpen] = useState(false);
  const { popupNotifications, dataUpdatedAt, refetch } = usePopupNotifications({
    freeze: open,
  });
  const [currentPageNum, setCurrentPageNum] = useState(1);
  const [confirmed, setConfirmed] = useState(false);
  // 表示条件が揃って候補の取り直しを始めた時刻（未開始なら undefined）
  const [refetchStartedAt, setRefetchStartedAt] = useState<number>();
  const currentNotification = popupNotifications.at(
    popupNotifications.length - currentPageNum,
  );

  const notificationEditor = useEditor({
    editable: false,
    extensions: extensionsWithLink,
    immediatelyRender: false,
    content: "",
  });

  useEffect(() => {
    if (notificationEditor && currentNotification?.body) {
      notificationEditor.commands.setContent(currentNotification.body);
    }
  }, [notificationEditor, currentNotification?.body]);

  // 送信済みIDを保持し、同じお知らせへの既読記録要求が2回以上出ないようにする
  const sentNotificationIdsRef = useRef<Set<string>>(new Set());

  const invalidateUnreadStatus = useInvalidateUnreadStatus();
  const { mutate: markRead } = medimoQC.useMutation(
    "post",
    "/v2/notifications/{notification_id}/read",
    {
      onSuccess: () => {
        invalidateUnreadStatus();
      },
      onError: (e, variables) => {
        console.error("お知らせの既読化に失敗しました:", e);
        // 失敗したお知らせは送信済みから外し、「戻る」→「次へ」や閉じる操作で再送できるようにする
        sentNotificationIdsRef.current.delete(
          variables.params.path.notification_id,
        );
      },
    },
  );

  const markCurrentRead = (notificationId: string) => {
    if (sentNotificationIdsRef.current.has(notificationId)) return;
    sentNotificationIdsRef.current.add(notificationId);
    // 既読記録は結果を待たず、UI操作をブロックしない
    markRead({ params: { path: { notification_id: notificationId } } });
  };

  const onOpenChange = (nextOpen: boolean) => {
    setConfirmed(true);
    setOpen(nextOpen);
    if (nextOpen) return;
    if (currentNotification) {
      markCurrentRead(currentNotification.id);
    }
  };

  const onClickNext = () => {
    if (currentNotification) {
      markCurrentRead(currentNotification.id);
    }
    setCurrentPageNum(currentPageNum + 1);
  };

  // 手元の候補は表示待ちの間に公開終了・既読になっている場合がある。表示条件が揃ったら
  // 候補を取り直し、取り直した結果が届いてから開く（取り直した候補が空なら開かない）
  useEffect(() => {
    if (pathname !== "/consults") return;
    if (transcribingStatus !== "inactive") return;
    if (confirmed) return;
    if (popupNotifications.length === 0) return;
    if (!isJustLoggedIn) return;
    if (refetchStartedAt === undefined) {
      setRefetchStartedAt(Date.now());
      refetch();
      return;
    }
    if (dataUpdatedAt <= refetchStartedAt) return;
    setOpen(true);
  }, [
    popupNotifications,
    confirmed,
    transcribingStatus,
    pathname,
    isJustLoggedIn,
    refetchStartedAt,
    dataUpdatedAt,
    refetch,
  ]);

  if (!currentNotification) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] w-[80%] flex-col gap-4 overflow-hidden p-4 md:min-w-[50vw] md:p-6">
        <DialogHeader>
          {/* 右上の閉じるボタンの下までタイトルが伸びないよう、左右に余白を取る
              （狭い幅では中央寄せなので左右同じ幅、sm 以上は左寄せなので右だけ） */}
          <DialogTitle className="px-6 text-lg sm:pl-0">
            {currentNotification.title}
          </DialogTitle>
          <DialogCloseButton />
        </DialogHeader>
        <DialogDescription className="min-h-0 flex-1 overflow-y-auto">
          <div className="whitespace-pre-wrap leading-6">
            <StyledEditor
              className="notification-text text-sm"
              editor={notificationEditor}
              disabled
            />
          </div>
        </DialogDescription>
        {popupNotifications.length > 1 && (
          <div className="flex justify-end gap-4">
            <Button
              disabled={currentPageNum === 1}
              onClick={() => {
                setCurrentPageNum(currentPageNum + -1);
              }}
            >
              戻る
            </Button>
            <Button
              disabled={currentPageNum === popupNotifications.length}
              onClick={onClickNext}
            >
              次へ
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
